import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Plus, Users, ChevronRight, Copy, Mail, Phone, MapPin,
  UserPlus, ArrowRight, RefreshCw, Search, Filter,
  FileText, Upload, ClipboardList, CreditCard, CheckCircle2,
  Send, FlaskConical, Stethoscope, Shield, Key, ExternalLink,
  MessageSquare, Lock, RotateCcw, Eye, Edit3,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'NEW', label: 'New', color: '#64748b' },
  { key: 'CONTACTED', label: 'Contacted', color: '#3b82f6' },
  { key: 'EVALUATION_READY', label: 'Eval Ready', color: '#8b5cf6' },
  { key: 'CLIENT_ACCOUNT_CREATED', label: 'Client Created', color: '#06b6d4' },
  { key: 'INTAKE_RECEIVED', label: 'Intake Received', color: '#14b8a6' },
  { key: 'PREDECODE_READY', label: 'PreDECODE Ready', color: '#22c55e' },
  { key: 'LABS_RECOMMENDED', label: 'Labs Recommended', color: '#84cc16' },
  { key: 'LABS_RECEIVED', label: 'Labs Received', color: '#eab308' },
  { key: 'DECODE_READY', label: 'DECODE Ready', color: '#f59e0b' },
  { key: 'DECISION_PENDING', label: 'Decision Pending', color: '#f97316' },
  { key: 'PAYMENT_CONFIRMED', label: 'Payment Confirmed', color: '#10b981' },
  { key: 'SUBSCRIBER_CONVERTED', label: 'Converted', color: '#059669' },
  { key: 'ON_HOLD', label: 'On Hold', color: '#6b7280' },
  { key: 'LOST', label: 'Lost', color: '#ef4444' },
];

const LEAD_SOURCES = [
  'website', 'referral', 'whatsapp', 'phone_call', 'walk_in',
  'camp_event', 'city_program', 'social_media', 'email', 'other',
];

const getStageColor = (key) => STAGES.find(s => s.key === key)?.color || '#64748b';
const getStageLabel = (key) => STAGES.find(s => s.key === key)?.label || key;

function LeadCard({ lead, onSelect }) {
  const stageColor = getStageColor(lead.pipeline_stage);
  return (
    <div data-testid={`lead-card-${lead.id}`}
      className="p-3 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer transition-all group"
      onClick={() => onSelect(lead)}>
      <div className="flex items-start justify-between mb-1.5">
        <p className="text-sm font-medium text-white/80 font-['Work_Sans'] truncate">{lead.name}</p>
        <Badge className="text-[8px] px-1.5 py-0 flex-shrink-0" style={{ backgroundColor: `${stageColor}25`, color: stageColor }}>
          {getStageLabel(lead.pipeline_stage)}
        </Badge>
      </div>
      {lead.email && <p className="text-[10px] text-white/35 truncate">{lead.email}</p>}
      <div className="flex items-center gap-3 mt-1.5">
        {lead.phone && <span className="text-[9px] text-white/25 flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{lead.phone}</span>}
        {lead.city && <span className="text-[9px] text-white/25 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{lead.city}</span>}
        {lead.condition && <span className="text-[9px] text-white/25 truncate">{lead.condition}</span>}
      </div>
      {lead.assigned_counselor_name && (
        <p className="text-[9px] text-blue-400/50 mt-1">Assigned: {lead.assigned_counselor_name}</p>
      )}
    </div>
  );
}

export default function AdminCRM() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('ALL');
  const [staffList, setStaffList] = useState([]);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', city: '', condition: '', message: '', lead_source: 'manual', campaign: '', centre: '' });
  const [actioning, setActioning] = useState(false);
  const [evalOverview, setEvalOverview] = useState(null);
  const [showIntakeDialog, setShowIntakeDialog] = useState(false);
  const [showPredecodeDialog, setShowPredecodeDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [intakeData, setIntakeData] = useState({});
  const [predecodeData, setPredecodeData] = useState({ summary: '', observations: '', lab_recommendations: '', notes: '' });
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailForm, setDetailForm] = useState({});

  const startEditDetails = () => {
    const lead = selectedLead;
    const u = lead?.client_user || {};
    setDetailForm({
      name: u.name || lead?.name || '',
      email: u.email || lead?.email || '',
      phone: lead?.phone || '',
      city: lead?.city || '',
      date_of_birth: u.date_of_birth || '',
      gender: u.gender || '',
    });
    setEditingDetails(true);
  };
  const saveDetails = async () => {
    const userId = selectedLead?.client_user_id;
    try {
      if (userId) {
        await axios.patch(`${API}/users/${userId}/personal-details`, detailForm);
      }
      await axios.patch(`${API}/crm/leads/${selectedLead.id}`, { name: detailForm.name, email: detailForm.email, phone: detailForm.phone, city: detailForm.city });
      toast.success('Details updated');
      setEditingDetails(false);
      loadData();
      const detail = await axios.get(`${API}/crm/leads/${selectedLead.id}`);
      setSelectedLead(detail.data);
      if (userId) loadAccessStatus(userId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, staffRes] = await Promise.all([
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
      ]);
      // Enrich leads with assigned counselor names
      const staff = Array.isArray(staffRes.data) ? staffRes.data : staffRes.data?.users || [];
      setStaffList(staff.filter(s => ['counselor', 'admin'].includes(s.role)));
      const counselorMap = {};
      staff.forEach(s => { counselorMap[s.id] = s.name; });
      const enriched = (Array.isArray(leadsRes.data) ? leadsRes.data : []).map(l => ({
        ...l,
        assigned_counselor_name: l.assigned_to ? counselorMap[l.assigned_to] : null,
      }));
      setLeads(enriched);
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Accept URL query params from the cockpit: ?stage=NEW or ?bucket=evaluation
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const stage = searchParams.get('stage');
    const bucket = searchParams.get('bucket');
    if (bucket === 'evaluation') setFilterStage('BUCKET_EVAL');
    else if (stage) setFilterStage(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCreateLead = async () => {
    if (!newLead.name) { toast.error('Name is required'); return; }
    setActioning(true);
    try {
      await axios.post(`${API}/crm/leads`, newLead);
      toast.success('Lead created');
      setShowCreateLead(false);
      setNewLead({ name: '', email: '', phone: '', city: '', condition: '', message: '', lead_source: 'manual', campaign: '', centre: '' });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActioning(false); }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await axios.patch(`${API}/inquiries/${leadId}`, { stage: newStage });
      toast.success(`Stage updated to ${getStageLabel(newStage)}`);
      loadData();
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => prev ? { ...prev, pipeline_stage: newStage } : null);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAssign = async (leadId, counselorId) => {
    try {
      await axios.put(`${API}/crm/leads/${leadId}/assign`, { assigned_to: counselorId });
      toast.success('Lead assigned');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCreateClientAccount = async (leadId) => {
    setActioning(true);
    try {
      const res = await axios.post(`${API}/crm/leads/${leadId}/create-client-account`);
      const data = res.data;
      toast.success(data.already_exists ? 'Client account already exists' : 'Account created — ready for internal setup');
      loadData();
      const detail = await axios.get(`${API}/crm/leads/${leadId}`);
      setSelectedLead(detail.data);
      loadEvalOverview(leadId);
      if (data.user_id) loadAccessStatus(data.user_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create client account'); }
    finally { setActioning(false); }
  };

  const [accessStatus, setAccessStatus] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [showTemplates, setShowTemplates] = useState(null);
  const [tempPwResult, setTempPwResult] = useState(null);

  const loadAccessStatus = async (userId) => {
    try {
      const res = await axios.get(`${API}/subscriber-access/${userId}/status`);
      setAccessStatus(res.data);
    } catch { setAccessStatus(null); }
  };

  useEffect(() => {
    if (selectedLead?.client_user_id) loadAccessStatus(selectedLead.client_user_id);
    else { setAccessStatus(null); setTemplates(null); setTempPwResult(null); }
  }, [selectedLead?.client_user_id]);

  const handleMarkReady = async () => {
    if (!selectedLead?.client_user_id) return;
    try {
      await axios.post(`${API}/subscriber-access/${selectedLead.client_user_id}/mark-ready`);
      toast.success('Account marked ready for access');
      loadAccessStatus(selectedLead.client_user_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleMarkSetup = async () => {
    if (!selectedLead?.client_user_id) return;
    try {
      await axios.post(`${API}/subscriber-access/${selectedLead.client_user_id}/mark-setup`);
      toast.success('Account returned to setup mode');
      loadAccessStatus(selectedLead.client_user_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleSendAccessLink = async () => {
    if (!selectedLead?.client_user_id) return;
    setActioning(true);
    try {
      const res = await axios.post(`${API}/subscriber-access/${selectedLead.client_user_id}/send-access-link`);
      toast.success(res.data.email_sent ? 'Fresh access link sent by email' : 'Access link generated (email not configured)');
      if (res.data.access_link) {
        navigator.clipboard.writeText(res.data.access_link).then(() => toast.info('Link copied to clipboard'));
      }
      loadAccessStatus(selectedLead.client_user_id);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActioning(false); }
  };
  const handleSetTempPassword = async () => {
    if (!selectedLead?.client_user_id) return;
    setActioning(true);
    try {
      const res = await axios.post(`${API}/subscriber-access/${selectedLead.client_user_id}/set-temp-password`);
      setTempPwResult(res.data);
      toast.success('Temporary password set — subscriber must change on first login');
      loadAccessStatus(selectedLead.client_user_id);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActioning(false); }
  };
  const handleResetAccess = async () => {
    if (!selectedLead?.client_user_id) return;
    try {
      await axios.post(`${API}/subscriber-access/${selectedLead.client_user_id}/reset-access`);
      toast.success('Access reset — send a new link or temp password when ready');
      setTempPwResult(null);
      loadAccessStatus(selectedLead.client_user_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleLoadTemplates = async () => {
    if (!selectedLead?.client_user_id) return;
    try {
      const res = await axios.get(`${API}/subscriber-access/${selectedLead.client_user_id}/message-templates`);
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
  };

  const loadEvalOverview = async (leadId) => {
    try {
      const res = await axios.get(`${API}/evaluation/overview/${leadId}`);
      setEvalOverview(res.data);
    } catch { setEvalOverview(null); }
  };

  const handleSaveIntake = async (leadId) => {
    setActioning(true);
    try {
      await axios.post(`${API}/evaluation/intake/${leadId}`, { sections: intakeData, is_complete: true });
      toast.success('Intake saved');
      setShowIntakeDialog(false);
      loadEvalOverview(leadId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActioning(false); }
  };

  const handleSavePredecode = async (leadId) => {
    setActioning(true);
    try {
      const obs = predecodeData.observations ? predecodeData.observations.split('\n').filter(Boolean) : [];
      const labs = predecodeData.lab_recommendations ? predecodeData.lab_recommendations.split('\n').filter(Boolean).map(l => ({ test: l, priority: 'medium' })) : [];
      await axios.post(`${API}/evaluation/predecode/${leadId}`, {
        summary: predecodeData.summary, observations: obs,
        lab_recommendations: labs, notes: predecodeData.notes,
      });
      toast.success('PreDECODE saved');
      setShowPredecodeDialog(false);
      loadEvalOverview(leadId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActioning(false); }
  };

  const handleMarkPredecodeSent = async (leadId, method) => {
    try {
      await axios.post(`${API}/evaluation/predecode/${leadId}/send`, { method });
      toast.success(`PreDECODE marked as sent (${method})`);
      loadEvalOverview(leadId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleValidatePayment = async (leadId, approved) => {
    try {
      await axios.post(`${API}/evaluation/payment/${leadId}/validate`, { approved });
      toast.success(approved ? 'Payment confirmed' : 'Payment rejected');
      loadEvalOverview(leadId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleConvert = async (leadId) => {
    if (!window.confirm('Convert this client to an active subscriber? This action is permanent.')) return;
    setActioning(true);
    try {
      await axios.post(`${API}/evaluation/convert/${leadId}`, { conditions: [selectedLead?.condition || 'parkinson'] });
      toast.success('Client converted to subscriber');
      loadEvalOverview(leadId);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActioning(false); }
  };

  const handleFileUpload = async (leadId, category, inputEl) => {
    const file = inputEl?.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', category);
    formData.append('label', file.name);
    try {
      const endpoint = category === 'historical_report'
        ? `${API}/evaluation/historical-reports/${leadId}/upload`
        : `${API}/evaluation/intake/${leadId}/upload`;
      formData.append('report_date', new Date().toISOString().split('T')[0]);
      await axios.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${category === 'historical_report' ? 'Historical report' : 'File'} uploaded`);
      loadEvalOverview(leadId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    inputEl.value = '';
  };

  // Filter leads
  const EVAL_STAGES = new Set([
    'CONTACTED', 'EVALUATION_READY', 'CLIENT_ACCOUNT_CREATED',
    'INTAKE_RECEIVED', 'PREDECODE_READY', 'LABS_RECOMMENDED',
    'LABS_RECEIVED', 'DECODE_READY', 'DECISION_PENDING', 'PAYMENT_CONFIRMED',
  ]);
  const filtered = leads.filter(l => {
    if (filterStage === 'BUCKET_EVAL') {
      if (!EVAL_STAGES.has(l.pipeline_stage)) return false;
    } else if (filterStage !== 'ALL' && l.pipeline_stage !== filterStage) {
      return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (l.name || '').toLowerCase().includes(term) ||
             (l.email || '').toLowerCase().includes(term) ||
             (l.phone || '').includes(term) ||
             (l.condition || '').toLowerCase().includes(term);
    }
    return true;
  });

  // Group by stage for pipeline view
  const stageCounts = {};
  leads.forEach(l => { stageCounts[l.pipeline_stage] = (stageCounts[l.pipeline_stage] || 0) + 1; });

  const isAdmin = user?.role === 'admin';
  const counselors = staffList.filter(s => s.role === 'counselor');

  return (
    <AdminLayout>
      <div data-testid="crm-page" className="p-6 max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">CRM Pipeline</h1>
            <p className="text-sm text-white/40 mt-0.5">{leads.length} leads in pipeline</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="crm-refresh" variant="outline" size="sm" onClick={loadData} disabled={loading}
              className="border-white/10 text-white/60"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <Button data-testid="crm-create-lead" size="sm" onClick={() => setShowCreateLead(true)}
              className="bg-[#C89B7B] hover:bg-[#B88A6A] text-[#080C14]">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Lead
            </Button>
          </div>
        </div>

        {/* Pipeline Stage Ribbon */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          <button data-testid="crm-filter-all"
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] transition-colors ${filterStage === 'ALL' ? 'bg-white/10 text-white' : 'bg-white/[0.03] text-white/40 hover:bg-white/[0.06]'}`}
            onClick={() => setFilterStage('ALL')}>
            All ({leads.length})
          </button>
          {(() => {
            const evalCount = leads.filter(l => EVAL_STAGES.has(l.pipeline_stage)).length;
            return (
              <button data-testid="crm-filter-bucket-evaluation"
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] transition-colors ${filterStage === 'BUCKET_EVAL' ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                style={{ backgroundColor: filterStage === 'BUCKET_EVAL' ? '#7E9EC830' : 'rgba(255,255,255,0.02)' }}
                onClick={() => setFilterStage('BUCKET_EVAL')}>
                Evaluation ({evalCount})
              </button>
            );
          })()}
          {STAGES.map(s => {
            const count = stageCounts[s.key] || 0;
            return (
              <button key={s.key} data-testid={`crm-filter-${s.key}`}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] transition-colors ${filterStage === s.key ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                style={{ backgroundColor: filterStage === s.key ? `${s.color}30` : 'rgba(255,255,255,0.02)' }}
                onClick={() => setFilterStage(s.key)}>
                {s.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
          <Input data-testid="crm-search"
            className="bg-white/[0.03] border-white/[0.06] text-white text-xs pl-9 h-9"
            placeholder="Search leads by name, email, phone, condition..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>

        {/* Lead List + Detail Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Lead List */}
          <div className="lg:col-span-1 space-y-1.5 max-h-[calc(100vh-320px)] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8"><RefreshCw className="w-4 h-4 text-white/20 animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-white/30 text-center py-8">No leads found</p>
            ) : (
              filtered.map(l => <LeadCard key={l.id} lead={l} onSelect={async (lead) => {
                try {
                  const res = await axios.get(`${API}/crm/leads/${lead.id}`);
                  setSelectedLead(res.data);
                  loadEvalOverview(lead.id);
                } catch { setSelectedLead(lead); setEvalOverview(null); }
              }} />)
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2 max-h-[calc(100vh-320px)] overflow-y-auto">
            {selectedLead ? (
              <Card className="bg-[#0d1117] border-white/5">
                <CardContent className="p-5 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 data-testid="lead-detail-name" className="text-lg font-semibold text-white">{selectedLead.name}</h2>
                      <p className="text-xs text-white/40">{selectedLead.id}</p>
                    </div>
                    <Badge data-testid="lead-detail-stage" className="text-[10px]"
                      style={{ backgroundColor: `${getStageColor(selectedLead.pipeline_stage)}25`, color: getStageColor(selectedLead.pipeline_stage) }}>
                      {getStageLabel(selectedLead.pipeline_stage)}
                    </Badge>
                  </div>

                  {/* Contact Info — Editable */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] text-white/30 uppercase tracking-wider">Personal Details</p>
                      {!editingDetails ? (
                        <Button data-testid="edit-details-btn" size="sm" variant="ghost" className="h-5 px-1.5 text-[8px] text-white/30 hover:text-white/60"
                          onClick={startEditDetails}>
                          <Edit3 className="w-3 h-3 mr-0.5" /> Edit
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button data-testid="save-details-btn" size="sm" className="h-5 px-2 text-[8px] bg-emerald-700/30 text-emerald-300" onClick={saveDetails}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-5 px-2 text-[8px] text-white/30" onClick={() => setEditingDetails(false)}>Cancel</Button>
                        </div>
                      )}
                    </div>
                    {editingDetails ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-[8px] text-white/30 block mb-0.5">Name</label>
                          <Input data-testid="edit-name" value={detailForm.name} onChange={e => setDetailForm(f => ({...f, name: e.target.value}))}
                            className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-7" /></div>
                        <div><label className="text-[8px] text-white/30 block mb-0.5">Email</label>
                          <Input data-testid="edit-email" type="email" value={detailForm.email} onChange={e => setDetailForm(f => ({...f, email: e.target.value}))}
                            className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-7" /></div>
                        <div><label className="text-[8px] text-white/30 block mb-0.5">Phone</label>
                          <Input data-testid="edit-phone" value={detailForm.phone} onChange={e => setDetailForm(f => ({...f, phone: e.target.value}))}
                            className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-7" /></div>
                        <div><label className="text-[8px] text-white/30 block mb-0.5">City</label>
                          <Input data-testid="edit-city" value={detailForm.city} onChange={e => setDetailForm(f => ({...f, city: e.target.value}))}
                            className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-7" /></div>
                        <div><label className="text-[8px] text-white/30 block mb-0.5">Date of Birth</label>
                          <Input data-testid="edit-dob" type="date" value={detailForm.date_of_birth} onChange={e => setDetailForm(f => ({...f, date_of_birth: e.target.value}))}
                            className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-7" /></div>
                        <div><label className="text-[8px] text-white/30 block mb-0.5">Gender</label>
                          <Select value={detailForm.gender} onValueChange={v => setDetailForm(f => ({...f, gender: v}))}>
                            <SelectTrigger className="bg-white/[0.03] border-white/[0.08] text-white text-xs h-7"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent className="bg-[#1a1f2e] border-white/10">
                              <SelectItem value="male" className="text-white text-xs">Male</SelectItem>
                              <SelectItem value="female" className="text-white text-xs">Female</SelectItem>
                              <SelectItem value="other" className="text-white text-xs">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {selectedLead.email && <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">Email</p><p className="text-xs text-white/70">{selectedLead.email}</p></div>}
                        {selectedLead.phone && <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">Phone</p><p className="text-xs text-white/70">{selectedLead.phone}</p></div>}
                        {selectedLead.city && <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">City</p><p className="text-xs text-white/70">{selectedLead.city}</p></div>}
                        {selectedLead.condition && <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">Condition</p><p className="text-xs text-white/70">{selectedLead.condition}</p></div>}
                        {selectedLead.lead_source && <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">Source</p><p className="text-xs text-white/70">{selectedLead.lead_source}</p></div>}
                        {selectedLead.campaign && <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">Campaign</p><p className="text-xs text-white/70">{selectedLead.campaign}</p></div>}
                      </div>
                    )}
                  </div>
                  {selectedLead.message && (
                    <div className="bg-white/[0.03] rounded p-2.5"><p className="text-[9px] text-white/30 mb-0.5">Message</p><p className="text-xs text-white/60">{selectedLead.message}</p></div>
                  )}

                  {/* Stage Control */}
                  <div className="border-t border-white/5 pt-3">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Pipeline Stage</p>
                    <Select value={selectedLead.pipeline_stage} onValueChange={(v) => handleStageChange(selectedLead.id, v)}>
                      <SelectTrigger data-testid="lead-stage-select" className="bg-white/[0.03] border-white/[0.06] text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0d1117] border-white/10 max-h-[300px]">
                        {STAGES.map(s => (
                          <SelectItem key={s.key} value={s.key} className="text-white text-xs">{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Assignment (Admin only) */}
                  {isAdmin && (
                    <div>
                      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Assign to Counselor</p>
                      <Select value={selectedLead.assigned_to || ''} onValueChange={(v) => handleAssign(selectedLead.id, v)}>
                        <SelectTrigger data-testid="lead-assign-select" className="bg-white/[0.03] border-white/[0.06] text-white text-xs">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d1117] border-white/10">
                          {counselors.map(c => (
                            <SelectItem key={c.id} value={c.id} className="text-white text-xs">{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* ── Subscriber Account & Access Control ── */}
                  <div className="border-t border-white/5 pt-3">
                    <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Subscriber Account</p>
                    {selectedLead.client_user_id ? (
                      <div className="space-y-3">
                        {/* A: Account Status */}
                        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Shield className="w-3.5 h-3.5 text-cyan-400" />
                              <span className="text-xs text-white/80">Account Status</span>
                            </div>
                            {accessStatus && (
                              <Badge data-testid="access-state-badge" className={`text-[8px] ${
                                accessStatus.account_access_state === 'active' ? 'bg-emerald-800/30 text-emerald-300' :
                                accessStatus.account_access_state === 'access_sent' ? 'bg-blue-800/30 text-blue-300' :
                                accessStatus.account_access_state === 'ready_for_access' ? 'bg-amber-800/30 text-amber-300' :
                                'bg-white/5 text-white/40'
                              }`}>
                                {accessStatus.account_access_state === 'internal_setup' ? 'Internal Setup' :
                                 accessStatus.account_access_state === 'ready_for_access' ? 'Ready for Access' :
                                 accessStatus.account_access_state === 'access_sent' ? 'Access Sent' :
                                 accessStatus.account_access_state === 'active' ? 'Active' : 'Setup'}
                              </Badge>
                            )}
                          </div>
                          {selectedLead.client_user && (
                            <div className="text-[10px] text-white/40 space-y-0.5">
                              <p>{selectedLead.client_user.name} — {selectedLead.client_user.email}</p>
                              {accessStatus?.access_sent_at && <p>Access sent: {new Date(accessStatus.access_sent_at).toLocaleDateString()}</p>}
                              {accessStatus?.last_login_at && <p>Last login: {new Date(accessStatus.last_login_at).toLocaleDateString()}</p>}
                              {accessStatus?.first_login_completed && <p className="text-emerald-400/60">First login completed</p>}
                            </div>
                          )}
                        </div>

                        {/* B: Internal Setup Control */}
                        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                          <p className="text-[9px] text-white/30 uppercase tracking-wider mb-2">Internal Setup</p>
                          <div className="flex flex-wrap gap-1.5">
                            <Button data-testid="open-subscriber-workspace" size="sm" variant="outline"
                              className="h-7 px-2.5 text-[9px] border-cyan-700/30 text-cyan-400 hover:bg-cyan-900/20"
                              onClick={() => window.open(`/admin/bio-context?subscriber=${selectedLead.client_user_id}`, '_blank')}>
                              <ExternalLink className="w-3 h-3 mr-1" /> Open Workspace
                            </Button>
                            {accessStatus?.account_access_state === 'internal_setup' ? (
                              <Button data-testid="mark-ready-btn" size="sm" variant="outline"
                                className="h-7 px-2.5 text-[9px] border-amber-700/30 text-amber-400 hover:bg-amber-900/20"
                                onClick={handleMarkReady}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Ready
                              </Button>
                            ) : accessStatus?.account_access_state === 'ready_for_access' ? (
                              <Button size="sm" variant="outline"
                                className="h-7 px-2.5 text-[9px] border-white/10 text-white/40"
                                onClick={handleMarkSetup}>
                                <RotateCcw className="w-3 h-3 mr-1" /> Back to Setup
                              </Button>
                            ) : null}
                          </div>
                        </div>

                        {/* C: Access Control */}
                        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                          <p className="text-[9px] text-white/30 uppercase tracking-wider mb-2">Subscriber Access Control</p>
                          <div className="flex flex-wrap gap-1.5">
                            <Button data-testid="send-access-link-btn" size="sm" disabled={actioning}
                              className="h-7 px-2.5 text-[9px] bg-[#4E7A6B] hover:bg-[#3D695B] text-white"
                              onClick={handleSendAccessLink}>
                              <Send className="w-3 h-3 mr-1" /> {actioning ? 'Sending...' : 'Send Fresh Access Link'}
                            </Button>
                            {isAdmin && (
                              <Button data-testid="set-temp-password-btn" size="sm" variant="outline" disabled={actioning}
                                className="h-7 px-2.5 text-[9px] border-violet-700/30 text-violet-400 hover:bg-violet-900/20"
                                onClick={handleSetTempPassword}>
                                <Key className="w-3 h-3 mr-1" /> Temp Password
                              </Button>
                            )}
                            <Button size="sm" variant="outline"
                              className="h-7 px-2.5 text-[9px] border-red-700/20 text-red-400/60 hover:bg-red-900/10"
                              onClick={handleResetAccess}>
                              <Lock className="w-3 h-3 mr-1" /> Reset Access
                            </Button>
                          </div>

                          {/* Temp password display */}
                          {tempPwResult?.temp_password && (
                            <div className="mt-2 p-2 bg-violet-900/10 border border-violet-800/20 rounded-md">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] text-white/50">Temporary Password:</span>
                                <Button size="sm" variant="ghost" className="h-5 px-1 text-[9px] text-white/50"
                                  onClick={() => { navigator.clipboard.writeText(tempPwResult.temp_password); toast.info('Password copied'); }}>
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-violet-300 font-mono mt-0.5" data-testid="temp-password-display">{tempPwResult.temp_password}</p>
                              <p className="text-[8px] text-white/25 mt-1">Subscriber must change this on first login</p>
                            </div>
                          )}

                          {accessStatus?.access_sent_count > 0 && (
                            <p className="text-[8px] text-white/20 mt-1.5">Access sent {accessStatus.access_sent_count} time{accessStatus.access_sent_count > 1 ? 's' : ''}</p>
                          )}
                        </div>

                        {/* D: Message Templates */}
                        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[9px] text-white/30 uppercase tracking-wider">Share Access Details</p>
                            <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[8px] text-white/30"
                              onClick={() => { handleLoadTemplates(); setShowTemplates(showTemplates ? null : 'fresh_link'); }}>
                              <MessageSquare className="w-3 h-3 mr-0.5" /> Templates
                            </Button>
                          </div>
                          {showTemplates && templates && (
                            <div className="space-y-2">
                              <div className="flex gap-1.5 mb-2">
                                <Button size="sm" variant={showTemplates === 'fresh_link' ? 'default' : 'outline'}
                                  className={`h-5 px-2 text-[8px] ${showTemplates === 'fresh_link' ? 'bg-cyan-800/40 text-cyan-300' : 'border-white/10 text-white/30'}`}
                                  onClick={() => setShowTemplates('fresh_link')}>Access Link</Button>
                                <Button size="sm" variant={showTemplates === 'temp_password' ? 'default' : 'outline'}
                                  className={`h-5 px-2 text-[8px] ${showTemplates === 'temp_password' ? 'bg-violet-800/40 text-violet-300' : 'border-white/10 text-white/30'}`}
                                  onClick={() => setShowTemplates('temp_password')}>Temp Password</Button>
                              </div>
                              {templates[showTemplates] && (
                                <div className="space-y-1.5">
                                  {['whatsapp', 'sms', 'email_body'].map(ch => (
                                    <div key={ch} className="flex items-start gap-1.5">
                                      <span className="text-[8px] text-white/20 w-14 pt-0.5 flex-shrink-0 capitalize">{ch === 'email_body' ? 'Email' : ch}</span>
                                      <p className="text-[9px] text-white/40 flex-1 line-clamp-2">{templates[showTemplates][ch]?.substring(0, 120)}...</p>
                                      <Button size="sm" variant="ghost" className="h-5 px-1 text-[8px] text-white/20 flex-shrink-0"
                                        onClick={() => { navigator.clipboard.writeText(templates[showTemplates][ch]); toast.info(`${ch} text copied`); }}>
                                        <Copy className="w-2.5 h-2.5" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Button data-testid="create-client-account"
                        size="sm" variant="outline" disabled={actioning || !selectedLead.email}
                        className="border-violet-700/30 text-violet-400 hover:bg-violet-900/20 text-xs"
                        onClick={() => handleCreateClientAccount(selectedLead.id)}>
                        <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                        {actioning ? 'Creating...' : 'Create Client Account'}
                      </Button>
                    )}
                    {!selectedLead.email && !selectedLead.client_user_id && (
                      <p className="text-[9px] text-amber-400/50 mt-1">Email required to create client account</p>
                    )}
                  </div>

                  {/* ── EVALUATION FLOW PANELS (B1-B4) ── */}
                  {selectedLead.client_user_id && evalOverview && (
                    <div className="border-t border-white/5 pt-3 space-y-3">
                      <p className="text-[10px] text-white/30 uppercase tracking-wider">Evaluation Journey</p>

                      {/* B1: Intake */}
                      <div className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <ClipboardList className="w-3.5 h-3.5 text-cyan-400/60" />
                            <span className="text-xs text-white/70">Detailed Intake</span>
                          </div>
                          <Badge className={`text-[8px] ${evalOverview.intake.is_complete ? 'bg-emerald-800/30 text-emerald-300' : evalOverview.intake.status === 'in_progress' ? 'bg-amber-800/30 text-amber-300' : 'bg-white/5 text-white/30'}`}>
                            {evalOverview.intake.is_complete ? 'Complete' : evalOverview.intake.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                          </Badge>
                        </div>
                        <div className="flex gap-1.5 mt-2">
                          <Button data-testid="eval-intake-btn" size="sm" variant="outline" className="h-6 px-2 text-[9px] border-white/10 text-white/50"
                            onClick={() => { setIntakeData({}); setShowIntakeDialog(true); }}>
                            {evalOverview.intake.is_complete ? 'View/Edit' : 'Fill Intake'}
                          </Button>
                          <label className="h-6 px-2 text-[9px] border border-white/10 rounded-md text-white/50 flex items-center gap-1 cursor-pointer hover:bg-white/5">
                            <Upload className="w-3 h-3" /> Upload
                            <input type="file" className="hidden" onChange={(e) => handleFileUpload(selectedLead.id, 'intake', e.target)} />
                          </label>
                          {evalOverview.intake.file_uploads > 0 && <span className="text-[9px] text-white/25 self-center">{evalOverview.intake.file_uploads} files</span>}
                        </div>
                      </div>

                      {/* B2: Historical Reports — Enhanced with type categorization */}
                      <div className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-violet-400/60" />
                            <span className="text-xs text-white/70">Historical Reports</span>
                          </div>
                          <Badge className={`text-[8px] ${evalOverview.historical_reports.count > 0 ? 'bg-emerald-800/30 text-emerald-300' : 'bg-white/5 text-white/30'}`}>
                            {evalOverview.historical_reports.count > 0 ? `${evalOverview.historical_reports.count} reports` : 'None'}
                          </Badge>
                        </div>
                        {evalOverview.historical_reports.count > 0 && (
                          <div className="text-[8px] text-white/25 mb-1.5">
                            {evalOverview.historical_reports.lab_sheets > 0 && <span className="mr-2">{evalOverview.historical_reports.lab_sheets} lab sheets</span>}
                            {evalOverview.historical_reports.categorized < evalOverview.historical_reports.count && (
                              <span className="text-amber-400/40">{evalOverview.historical_reports.count - evalOverview.historical_reports.categorized} uncategorized</span>
                            )}
                          </div>
                        )}
                        <label className="mt-1 h-6 px-2 text-[9px] border border-white/10 rounded-md text-white/50 flex items-center gap-1 cursor-pointer hover:bg-white/5 w-fit">
                          <Upload className="w-3 h-3" /> Upload Report
                          <input type="file" className="hidden" data-testid="eval-hist-upload"
                            onChange={(e) => handleFileUpload(selectedLead.id, 'historical_report', e.target)} />
                        </label>
                      </div>

                      {/* B3: PreDECODE + Lab Recommendation — Enhanced with PDF upload */}
                      <div className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Stethoscope className="w-3.5 h-3.5 text-amber-400/60" />
                            <span className="text-xs text-white/70">PreDECODE + Lab Recommendation</span>
                          </div>
                          <Badge className={`text-[8px] ${evalOverview.predecode.sent_to_client ? 'bg-emerald-800/30 text-emerald-300' : evalOverview.predecode.prepared ? 'bg-amber-800/30 text-amber-300' : 'bg-white/5 text-white/30'}`}>
                            {evalOverview.predecode.sent_to_client ? 'Sent' : evalOverview.predecode.prepared ? 'Prepared' : 'Not Started'}
                          </Badge>
                        </div>
                        {(evalOverview.predecode.has_pdf || evalOverview.predecode.has_lab_rec) && (
                          <div className="text-[8px] text-white/25 mb-1.5">
                            {evalOverview.predecode.has_pdf && <span className="text-emerald-400/40 mr-2">PreDECODE PDF</span>}
                            {evalOverview.predecode.has_lab_rec && <span className="text-cyan-400/40">Lab Recommendation</span>}
                          </div>
                        )}
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {isAdmin && (
                            <Button data-testid="eval-predecode-btn" size="sm" variant="outline" className="h-6 px-2 text-[9px] border-white/10 text-white/50"
                              onClick={() => setShowPredecodeDialog(true)}>
                              {evalOverview.predecode.prepared ? 'Edit PreDECODE' : 'Prepare PreDECODE'}
                            </Button>
                          )}
                          <label className="h-6 px-2 text-[9px] border border-amber-700/20 rounded-md text-amber-400/70 flex items-center gap-1 cursor-pointer hover:bg-amber-900/10">
                            <Upload className="w-3 h-3" /> PreDECODE PDF
                            <input type="file" accept=".pdf" className="hidden" data-testid="eval-predecode-pdf-upload"
                              onChange={async (e) => {
                                const file = e.target.files?.[0]; if (!file) return;
                                const fd = new FormData(); fd.append('file', file); fd.append('doc_type', 'predecode');
                                try { await axios.post(`${API}/evaluation/predecode/${selectedLead.id}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('PreDECODE PDF uploaded'); loadEvalOverview(selectedLead.id); } catch { toast.error('Upload failed'); }
                                e.target.value = '';
                              }} />
                          </label>
                          <label className="h-6 px-2 text-[9px] border border-cyan-700/20 rounded-md text-cyan-400/70 flex items-center gap-1 cursor-pointer hover:bg-cyan-900/10">
                            <Upload className="w-3 h-3" /> Lab Rec.
                            <input type="file" accept=".pdf,.xlsx,.csv" className="hidden" data-testid="eval-labrec-upload"
                              onChange={async (e) => {
                                const file = e.target.files?.[0]; if (!file) return;
                                const fd = new FormData(); fd.append('file', file); fd.append('doc_type', 'lab_recommendation');
                                try { await axios.post(`${API}/evaluation/predecode/${selectedLead.id}/upload-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Lab recommendation uploaded'); loadEvalOverview(selectedLead.id); } catch { toast.error('Upload failed'); }
                                e.target.value = '';
                              }} />
                          </label>
                          {evalOverview.predecode.prepared && !evalOverview.predecode.sent_to_client && (
                            <>
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[9px] border-blue-700/20 text-blue-400"
                                onClick={() => handleMarkPredecodeSent(selectedLead.id, 'email')}>
                                <Mail className="w-3 h-3 mr-1" /> Email
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[9px] border-emerald-700/20 text-emerald-400"
                                onClick={() => handleMarkPredecodeSent(selectedLead.id, 'whatsapp')}>
                                <Send className="w-3 h-3 mr-1" /> WhatsApp
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* B5: DECODE Report */}
                      <div className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <FlaskConical className="w-3.5 h-3.5 text-rose-400/60" />
                            <span className="text-xs text-white/70">DECODE Report</span>
                          </div>
                          <Badge data-testid="eval-decode-badge" className={`text-[8px] ${evalOverview.decode?.has_decode ? 'bg-emerald-800/30 text-emerald-300' : 'bg-white/5 text-white/30'}`}>
                            {evalOverview.decode?.has_decode ? (evalOverview.decode.mode === 'manual_upload' ? 'Uploaded' : 'Wizard') : 'Not Started'}
                          </Badge>
                        </div>
                        {evalOverview.decode?.uploaded_count > 0 && (
                          <p className="text-[8px] text-white/25 mb-1">{evalOverview.decode.uploaded_count} uploaded PDF{evalOverview.decode.uploaded_count > 1 ? 's' : ''}</p>
                        )}
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {isAdmin && (
                            <label className="h-6 px-2 text-[9px] border border-rose-700/20 rounded-md text-rose-400/70 flex items-center gap-1 cursor-pointer hover:bg-rose-900/10">
                              <Upload className="w-3 h-3" /> Upload DECODE PDF
                              <input type="file" accept=".pdf" className="hidden" data-testid="eval-decode-upload"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0]; if (!file) return;
                                  const fd = new FormData(); fd.append('file', file); fd.append('version', '1');
                                  try { await axios.post(`${API}/evaluation/decode/${selectedLead.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('DECODE PDF uploaded'); loadEvalOverview(selectedLead.id); loadData(); } catch { toast.error('Upload failed'); }
                                  e.target.value = '';
                                }} />
                            </label>
                          )}
                          {isAdmin && selectedLead.client_user_id && (
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[9px] border-white/10 text-white/40"
                              onClick={() => window.open(`/admin/decode-wizard?subscriber=${selectedLead.client_user_id}`, '_blank')}>
                              Open DECODE Workspace
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* B6: Lab Intelligence */}
                      <div className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <FlaskConical className="w-3.5 h-3.5 text-teal-400/60" />
                            <span className="text-xs text-white/70">Lab Intelligence</span>
                          </div>
                          <Badge className={`text-[8px] ${evalOverview.lab_intelligence?.has_data ? 'bg-emerald-800/30 text-emerald-300' : 'bg-white/5 text-white/30'}`}>
                            {evalOverview.lab_intelligence?.has_data
                              ? `${evalOverview.lab_intelligence.validated_reports}/${evalOverview.lab_intelligence.total_reports} validated`
                              : 'No Data'}
                          </Badge>
                        </div>
                        <div className="flex gap-1.5 mt-2">
                          {selectedLead.client_user_id && (
                            <Button data-testid="eval-lab-intel-btn" size="sm" variant="outline" className="h-6 px-2 text-[9px] border-teal-700/20 text-teal-400"
                              onClick={() => window.open(`/admin/lab-intelligence?subscriber=${selectedLead.client_user_id}`, '_blank')}>
                              <ExternalLink className="w-3 h-3 mr-1" /> Open Lab Intelligence
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* B4: Payment + Conversion */}
                      <div className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-3.5 h-3.5 text-emerald-400/60" />
                            <span className="text-xs text-white/70">Payment & Conversion</span>
                          </div>
                          <Badge className={`text-[8px] ${evalOverview.conversion.converted ? 'bg-emerald-800/30 text-emerald-300' : evalOverview.payment.status === 'confirmed' ? 'bg-amber-800/30 text-amber-300' : 'bg-white/5 text-white/30'}`}>
                            {evalOverview.conversion.converted ? 'Converted' : evalOverview.payment.status === 'confirmed' ? 'Payment Confirmed' : evalOverview.payment.status === 'uploaded' ? 'Pending Validation' : 'Awaiting'}
                          </Badge>
                        </div>
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {!evalOverview.conversion.converted && (
                            <label className="h-6 px-2 text-[9px] border border-white/10 rounded-md text-white/50 flex items-center gap-1 cursor-pointer hover:bg-white/5">
                              <Upload className="w-3 h-3" /> Payment Receipt
                              <input type="file" className="hidden" data-testid="eval-payment-upload"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  fd.append('payment_method', 'bank_transfer');
                                  fd.append('payment_date', new Date().toISOString().split('T')[0]);
                                  try {
                                    await axios.post(`${API}/evaluation/payment/${selectedLead.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    toast.success('Payment receipt uploaded');
                                    loadEvalOverview(selectedLead.id); loadData();
                                  } catch (err) { toast.error('Upload failed'); }
                                  e.target.value = '';
                                }} />
                            </label>
                          )}
                          {isAdmin && evalOverview.payment.status === 'uploaded' && (
                            <>
                              <Button data-testid="eval-validate-payment" size="sm" variant="outline" className="h-6 px-2 text-[9px] border-emerald-700/20 text-emerald-400"
                                onClick={() => handleValidatePayment(selectedLead.id, true)}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Validate
                              </Button>
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[9px] border-red-700/20 text-red-400"
                                onClick={() => handleValidatePayment(selectedLead.id, false)}>Reject</Button>
                            </>
                          )}
                          {isAdmin && evalOverview.payment.status === 'confirmed' && !evalOverview.conversion.converted && (
                            <Button data-testid="eval-convert-btn" size="sm" className="h-6 px-2.5 text-[9px] bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleConvert(selectedLead.id)} disabled={actioning}>
                              <ArrowRight className="w-3 h-3 mr-1" /> Convert to Subscriber
                            </Button>
                          )}
                          {evalOverview.conversion.converted && (
                            <span className="text-[10px] text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Subscriber Active</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-center h-64 text-white/20">
                <div className="text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Select a lead to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Lead Dialog */}
        <Dialog open={showCreateLead} onOpenChange={setShowCreateLead}>
          <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle>Add New Lead</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
              <div>
                <p className="text-[10px] text-white/40 mb-1">Full Name *</p>
                <Input data-testid="new-lead-name" className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                  value={newLead.name} onChange={e => setNewLead(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Email</p>
                  <Input data-testid="new-lead-email" type="email" className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                    value={newLead.email} onChange={e => setNewLead(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Phone</p>
                  <Input data-testid="new-lead-phone" className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                    value={newLead.phone} onChange={e => setNewLead(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-white/40 mb-1">City</p>
                  <Input data-testid="new-lead-city" className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                    value={newLead.city} onChange={e => setNewLead(p => ({ ...p, city: e.target.value }))} />
                </div>
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Lead Source</p>
                  <Select value={newLead.lead_source} onValueChange={v => setNewLead(p => ({ ...p, lead_source: v }))}>
                    <SelectTrigger data-testid="new-lead-source" className="bg-white/[0.03] border-white/[0.06] text-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0d1117] border-white/10">
                      {LEAD_SOURCES.map(s => <SelectItem key={s} value={s} className="text-white text-xs">{s.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Condition / Concern</p>
                <Input data-testid="new-lead-condition" className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                  placeholder="Free text — any condition or concern"
                  value={newLead.condition} onChange={e => setNewLead(p => ({ ...p, condition: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Campaign / Program</p>
                  <Input className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                    value={newLead.campaign} onChange={e => setNewLead(p => ({ ...p, campaign: e.target.value }))} />
                </div>
                <div>
                  <p className="text-[10px] text-white/40 mb-1">Centre</p>
                  <Input className="bg-white/[0.03] border-white/[0.06] text-white text-xs"
                    value={newLead.centre} onChange={e => setNewLead(p => ({ ...p, centre: e.target.value }))} />
                </div>
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Message / Notes</p>
                <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[60px]"
                  value={newLead.message} onChange={e => setNewLead(p => ({ ...p, message: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCreateLead(false)} className="text-white/60">Cancel</Button>
              <Button data-testid="save-new-lead" onClick={handleCreateLead} disabled={actioning || !newLead.name}
                className="bg-[#C89B7B] hover:bg-[#B88A6A] text-[#080C14]">
                {actioning ? 'Creating...' : 'Create Lead'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Intake Dialog */}
        <Dialog open={showIntakeDialog} onOpenChange={setShowIntakeDialog}>
          <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-2xl max-h-[80vh]">
            <DialogHeader><DialogTitle>Detailed Intake Form</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2 overflow-y-auto max-h-[60vh]">
              {['life_family_context', 'sleep_rhythm_daily_life', 'movement_activity', 'dietary_hydration',
                'medication_therapy_history', 'bas_biological_arrival_state', 'nas_nutritional_arrival_state',
                'tas_therapeutic_arrival_state', 'intersection_layer', 'reproductive_hormonal',
                'family_epigenetic_history', 'goals_fears_alignment', 'practical_financial_reality',
                'communication_commitment'].map(section => (
                <div key={section}>
                  <p className="text-[10px] text-white/40 mb-1 uppercase tracking-wider">{section.replace(/_/g, ' ')}</p>
                  <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[50px]"
                    placeholder={`Enter ${section.replace(/_/g, ' ')} details...`}
                    value={typeof intakeData[section] === 'string' ? intakeData[section] : JSON.stringify(intakeData[section] || '')}
                    onChange={e => setIntakeData(p => ({ ...p, [section]: e.target.value }))} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowIntakeDialog(false)} className="text-white/60">Cancel</Button>
              <Button data-testid="save-intake" onClick={() => handleSaveIntake(selectedLead?.id)} disabled={actioning}
                className="bg-cyan-600 hover:bg-cyan-700">{actioning ? 'Saving...' : 'Save Intake'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PreDECODE Dialog */}
        <Dialog open={showPredecodeDialog} onOpenChange={setShowPredecodeDialog}>
          <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle>PreDECODE + Lab Recommendation</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <p className="text-[10px] text-white/40 mb-1">Summary</p>
                <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[80px]"
                  placeholder="Initial assessment summary..."
                  value={predecodeData.summary} onChange={e => setPredecodeData(p => ({ ...p, summary: e.target.value }))} />
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Observations (one per line)</p>
                <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[60px]"
                  placeholder="Poor sleep quality&#10;Low energy baseline&#10;3+ years on medication"
                  value={predecodeData.observations} onChange={e => setPredecodeData(p => ({ ...p, observations: e.target.value }))} />
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Lab Recommendations (one per line)</p>
                <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[60px]"
                  placeholder="Complete Blood Panel&#10;Gut Microbiome Analysis&#10;Vitamin D levels"
                  value={predecodeData.lab_recommendations} onChange={e => setPredecodeData(p => ({ ...p, lab_recommendations: e.target.value }))} />
              </div>
              <div>
                <p className="text-[10px] text-white/40 mb-1">Notes</p>
                <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[40px]"
                  value={predecodeData.notes} onChange={e => setPredecodeData(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowPredecodeDialog(false)} className="text-white/60">Cancel</Button>
              <Button data-testid="save-predecode" onClick={() => handleSavePredecode(selectedLead?.id)} disabled={actioning}
                className="bg-amber-600 hover:bg-amber-700">{actioning ? 'Saving...' : 'Save PreDECODE'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
